import type { WorkflowField } from './workflows.types.js';

// The paths the actions service gives a step's Primary KPI. Flight Check renders
// the shared `kpis` param, which tRPC narrows to a single select for it; a step
// that keeps it a multiselect is a different control and is left alone.
const PRIMARY_KPI_KEYS = ['kpi', 'kpis'];

export type FieldValue = string | number | string[];
export type FieldValues = Record<string, FieldValue>;

export function defaultFieldValues(fields: WorkflowField[]): FieldValues {
  const values: FieldValues = {};
  for (const field of fields) {
    if (field.kind === 'collapsible') Object.assign(values, defaultFieldValues(field.fields));
    else if (field.kind === 'exclusiveGroup') {
      // The group's own value names the active option; only that one is submitted.
      const [first] = field.options;
      if (first !== undefined) values[field.key] = first.key;
      Object.assign(values, defaultFieldValues(field.options));
    } else if (field.kind !== 'upload' && field.default !== null) {
      // An upload has no default: the files are collected, not defaulted.
      values[field.key] = field.default;
    }
  }
  return values;
}

export function activeExclusiveOption(
  field: Extract<WorkflowField, { kind: 'exclusiveGroup' }>,
  values: FieldValues,
): Extract<WorkflowField, { kind: 'number' }> | undefined {
  const selected = values[field.key];
  return field.options.find((option) => option.key === selected) ?? field.options[0];
}

function acceptsAsPrimaryKpi(field: WorkflowField, kpi: string): boolean {
  if (field.kind !== 'select' || !PRIMARY_KPI_KEYS.includes(field.key)) return false;
  const source = field.optionsSource;
  if (source === null || source.metric !== true) return false;
  return source.metricNames === null || source.metricNames.includes(kpi);
}

function isSet(value: FieldValue | undefined): boolean {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'string' ? value.trim() !== '' : true;
}

export function missingRequired(fields: WorkflowField[], values: FieldValues): string[] {
  return fields.flatMap((field) => {
    if (field.kind === 'collapsible') return missingRequired(field.fields, values);
    if (field.kind === 'exclusiveGroup') {
      const active = activeExclusiveOption(field, values);
      return active === undefined ? [] : missingRequired([active], values);
    }
    if (!field.required || isSet(values[field.key])) return [];
    return [field.label];
  });
}

function leavesOf(fields: WorkflowField[]): WorkflowField[] {
  return fields.flatMap((field) => (field.kind === 'collapsible' ? field.fields : [field]));
}

// A field whose options are the values of a sibling's filter goes back to its
// default when that sibling changes: the old picks name another filter's values.
export function withFieldValue(
  fields: WorkflowField[],
  values: FieldValues,
  key: string,
  value: FieldValue,
): FieldValues {
  const next = { ...values, [key]: value };
  if (values[key] === value) return next;
  for (const field of leavesOf(fields)) {
    if (
      (field.kind === 'select' || field.kind === 'segmented' || field.kind === 'multiselect') &&
      field.optionsSource?.filterFrom?.field === key
    ) {
      next[field.key] = field.default;
    }
  }
  return next;
}

// An unset Primary KPI opens on the scope's Leading KPI. It is a starting value,
// not the field default, so the run is submitted with it.
export function withPrimaryKpi(
  fields: WorkflowField[],
  values: FieldValues,
  leadingKpi: string,
): FieldValues {
  if (leadingKpi === '') return values;
  const seeded = { ...values };
  for (const field of fields) {
    if (field.kind === 'collapsible') {
      Object.assign(seeded, withPrimaryKpi(field.fields, seeded, leadingKpi));
    } else if (acceptsAsPrimaryKpi(field, leadingKpi) && !isSet(seeded[field.key])) {
      seeded[field.key] = leadingKpi;
    }
  }
  return seeded;
}
